import { ChangeDetectorRef, Component, LOCALE_ID, OnDestroy, OnInit, inject } from "@angular/core";
import { MAT_DATE_FORMATS, DateAdapter } from "@angular/material/core";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { AlarmModel, AlarmModelTimeWindow } from "app/models/alarm.model";
import { DataTypeModelKey, isRetiredDataTypeModelKey } from "app/models/dataType.model";
import { DeviceDataModel } from "app/models/deviceData.model";
import { ProjectModel } from "app/models/project.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { AlarmService } from "app/services/api/alarm.service";
import { DataService } from "app/services/api/data.service";
import { ProjectAlarmService } from "app/services/api/projectAlarm.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { ProjectService } from "app/services/api/project.service";
import { ContextService } from "app/services/context.service";
import { buildAlarmThresholdSeries } from "app/shared/alarm-period-rendering";
import { EmmaDateAdapter } from "app/shared/date.adapter";
import * as Highcharts from "highcharts/highstock";
import * as moment from "moment";
import { ToastrService } from "ngx-toastr";
import { Subscription, forkJoin, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { locale as deviceCatalan } from "../../devices/i18n/ca";
import { locale as deviceEnglish } from "../../devices/i18n/en";
import { locale as deviceSpanish } from "../../devices/i18n/es";
import { locale as catalan } from "../i18n/ca";
import { locale as english } from "../i18n/en";
import { locale as spanish } from "../i18n/es";

type ChartSeriesLineStyle = "Solid" | "ShortDash" | "Dot";
type ChartSeriesVariant = "base" | "L10" | "L90";
type DeviceChartData = Partial<Record<DataTypeModelKey, DeviceDataModel[]>>;

interface ChartMetricOption {
    key: string;
    dataType: DataTypeModelKey;
    variant: ChartSeriesVariant;
    label: string;
}

interface ProjectChartSeriesConfig {
    key: string;
    deviceId: string;
    deviceName: string;
    dataType: DataTypeModelKey;
    variant: ChartSeriesVariant;
    color: string;
    lineStyle: ChartSeriesLineStyle;
}

interface ChartAlarmOption {
    key: string;
    deviceId: string;
    label: string;
    alarm: AlarmModel;
    selected: boolean;
}

const invalidProjectChartMetrics = new Set<DataTypeModelKey>([
    DataTypeModelKey.overload,
    DataTypeModelKey.underrange,
    DataTypeModelKey.spectrum13Octave,
    DataTypeModelKey.spectrum13Octave1Minute,
    DataTypeModelKey.onlineDevices,
    DataTypeModelKey.eventStream,
]);

const defaultPalette = [
    "#0C65E8",
    "#1D4ED8",
    "#2563EB",
    "#7C3AED",
    "#0F766E",
    "#16A34A",
    "#D97706",
    "#DC2626",
    "#9333EA",
    "#0369A1",
    "#B45309",
    "#BE185D",
];

@Component({
    selector: "app-charts",
    templateUrl: "./charts.component.html",
    styleUrls: ["./charts.component.scss"],
    providers: [
        { provide: DateAdapter, useClass: EmmaDateAdapter },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: "emma",
                },
                display: {
                    dateInput: "emma",
                    monthYearLabel: "MMM YYYY",
                    dateA11yLabel: "LL",
                    monthYearA11yLabel: "MMMM YYYY",
                },
            },
        },
    ],
    standalone: false,
})
export class ChartsComponent implements OnInit, OnDestroy {
    readonly Highcharts = Highcharts;
    readonly chartLineStyleOptions: Array<{ value: ChartSeriesLineStyle; labelKey: string }> = [
        { value: "Solid", labelKey: "REPORT.LineStyleSolid" },
        { value: "ShortDash", labelKey: "REPORT.LineStyleDashed" },
        { value: "Dot", labelKey: "REPORT.LineStyleDotted" },
    ];

    currentProjectId: string | null = null;
    project: ProjectModel | null = null;
    devices: ProjectDeviceModel[] = [];
    availableMetricOptions: ChartMetricOption[] = [];
    selectedSeriesDeviceId: string | null = null;
    selectedMetricKey: string | null = null;
    seriesConfigs: ProjectChartSeriesConfig[] = [];
    alarmOptions: ChartAlarmOption[] = [];
    chartDataByDevice: Record<string, DeviceChartData> = {};

    dateRange = "last24hours";
    startDate = moment().subtract(24, "hours");
    startTime = "00:00";
    endDate = moment();
    endTime = "23:59";
    endDateMin = null;
    endDateMax = null;
    groupingData = "5minutes";
    enableInstantValues = false;
    loading = false;
    hasChartData = false;

    chart = this.createBaseChartOptions();
    chartUpdate = true;
    chartRef: Highcharts.Chart | null = null;

    private readonly subscriptions = new Subscription();
    private readonly translation = inject(FuseTranslationLoaderService);
    private readonly contextService = inject(ContextService);
    private readonly projectService = inject(ProjectService);
    private readonly projectDeviceService = inject(ProjectDeviceService);
    private readonly dataService = inject(DataService);
    private readonly alarmService = inject(AlarmService);
    private readonly projectAlarmService = inject(ProjectAlarmService);
    private readonly toastr = inject(ToastrService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly locale = inject(LOCALE_ID);

    chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
        this.chartRef = chart;
        this.chartRef.reflow();
    };

    ngOnInit() {
        this.translation.loadTranslations(english, spanish, catalan, deviceEnglish, deviceSpanish, deviceCatalan);

        this.currentProjectId = this.contextService.currentProject;
        this.reloadProjectContext();
        this.subscriptions.add(
            this.contextService.onCurrentProjectChange.subscribe((projectId) => {
                this.currentProjectId = projectId;
                this.reloadProjectContext();
            }),
        );
        this.subscriptions.add(this.contextService.onForceDataRefresh.subscribe(() => this.reloadProjectContext()));
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    get showNoProjectState(): boolean {
        return !this.currentProjectId;
    }

    get showNoDevicesState(): boolean {
        return !this.loading && !!this.currentProjectId && this.devices.length === 0;
    }

    get canAddSeries(): boolean {
        if (!this.selectedSeriesDeviceId || !this.selectedMetricKey) {
            return false;
        }

        return !this.seriesConfigs.some((series) => series.key === this.buildSeriesKeyFromSelection());
    }

    refreshChartLayout() {
        this.chartRef?.reflow();
    }

    onDateRangeChange() {
        this.updateEndDate();
        this.loadChartData();
    }

    onSeriesBuilderDeviceChange() {
        this.syncAvailableMetricOptions();
    }

    onSeriesStyleChange() {
        this.rebuildChart();
    }

    onAlarmSelectionChange() {
        this.rebuildChart();
    }

    addSeries() {
        const device = this.devices.find((candidate) => candidate.id === this.selectedSeriesDeviceId);
        const metric = this.availableMetricOptions.find((candidate) => candidate.key === this.selectedMetricKey);
        if (!device || !metric || !this.canAddSeries) {
            return;
        }

        this.seriesConfigs = [
            ...this.seriesConfigs,
            {
                key: this.buildSeriesKey(device.id, metric.dataType, metric.variant),
                deviceId: device.id,
                deviceName: device.deviceName,
                dataType: metric.dataType,
                variant: metric.variant,
                color: this.getNextSeriesColor(),
                lineStyle: this.getDefaultLineStyle(metric.variant),
            },
        ];

        this.refreshSeriesDrivenState();
    }

    removeSeries(key: string) {
        this.seriesConfigs = this.seriesConfigs.filter((series) => series.key !== key);
        this.refreshSeriesDrivenState();
    }

    updateEndDate() {
        this.endDateMin = this.startDate;
        this.endDateMax = moment(this.startDate).add(7, "days");

        if (this.endDateMax.isAfter(moment())) {
            this.endDateMax = moment();
        }

        if (!this.endDate || moment(this.endDate).isAfter(this.endDateMax)) {
            this.endDate = moment(this.endDateMax);
        }
    }

    private reloadProjectContext() {
        if (!this.currentProjectId) {
            this.project = null;
            this.devices = [];
            this.availableMetricOptions = [];
            this.selectedSeriesDeviceId = null;
            this.selectedMetricKey = null;
            this.seriesConfigs = [];
            this.alarmOptions = [];
            this.chartDataByDevice = {};
            this.rebuildChart();
            return;
        }

        this.loading = true;
        forkJoin({
            project: this.projectService.get(this.currentProjectId),
            devices: this.projectDeviceService.getAll(this.currentProjectId),
        }).subscribe(
            ({ project, devices }) => {
                this.project = project;
                const format = project?.dateformat?.toUpperCase();
                if (format) {
                    const [dateFormat] = format.split(" ");
                    EmmaDateAdapter.dateFormat = dateFormat;
                }

                this.devices = [...devices].sort((left, right) => left.deviceName.localeCompare(right.deviceName, this.locale));

                const validDeviceMap = new Map(this.devices.map((device) => [device.id, device]));
                this.seriesConfigs = this.seriesConfigs
                    .filter((series) => {
                        const device = validDeviceMap.get(series.deviceId);
                        return !!device && this.getValidDeviceFields(device).includes(series.dataType);
                    })
                    .map((series) => ({
                        ...series,
                        deviceName: validDeviceMap.get(series.deviceId)!.deviceName,
                    }));

                if (!this.selectedSeriesDeviceId || !validDeviceMap.has(this.selectedSeriesDeviceId)) {
                    this.selectedSeriesDeviceId = this.devices[0]?.id ?? null;
                }

                this.syncAvailableMetricOptions();
                this.refreshSeriesDrivenState();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.loading = false;
                this.cdr.detectChanges();
            },
        );
    }

    private syncAvailableMetricOptions() {
        const device = this.devices.find((candidate) => candidate.id === this.selectedSeriesDeviceId);
        if (!device) {
            this.availableMetricOptions = [];
            this.selectedMetricKey = null;
            return;
        }

        this.availableMetricOptions = this.getValidDeviceFields(device).flatMap((field) => {
            const baseLabel = this.translation.translateService.instant(`FIELD.${field}`);
            const options: ChartMetricOption[] = [
                {
                    key: this.buildMetricOptionKey(field, "base"),
                    dataType: field,
                    variant: "base",
                    label: baseLabel,
                },
            ];

            if (this.usesLogarithmicAverage(field)) {
                options.push(
                    {
                        key: this.buildMetricOptionKey(field, "L10"),
                        dataType: field,
                        variant: "L10",
                        label: `${baseLabel} (L10)`,
                    },
                    {
                        key: this.buildMetricOptionKey(field, "L90"),
                        dataType: field,
                        variant: "L90",
                        label: `${baseLabel} (L90)`,
                    },
                );
            }

            return options;
        });

        const metricKeys = new Set(this.availableMetricOptions.map((option) => option.key));
        if (!this.selectedMetricKey || !metricKeys.has(this.selectedMetricKey)) {
            this.selectedMetricKey = this.availableMetricOptions[0]?.key ?? null;
        }
    }

    private refreshSeriesDrivenState() {
        this.loadAlarmOptions();
        this.loadChartData();
    }

    private loadAlarmOptions() {
        if (!this.currentProjectId || this.seriesDevices.length === 0) {
            this.alarmOptions = [];
            this.rebuildChart();
            return;
        }

        const selectedState = new Map(this.alarmOptions.map((alarm) => [alarm.key, alarm.selected]));
        const fieldsByDevice = this.getSeriesFieldsByDevice();
        forkJoin(
            this.seriesDevices.map((device) =>
                forkJoin({
                    deviceAlarms: this.alarmService.getAllDevice(device.id).pipe(catchError(() => of([]))),
                    projectAlarms: this.projectAlarmService.getAllProject(this.currentProjectId!, device.id).pipe(catchError(() => of([]))),
                }).pipe(
                    catchError(() =>
                        of({
                            deviceAlarms: [],
                            projectAlarms: [],
                        }),
                    ),
                ),
            ),
        ).subscribe((deviceAlarmGroups) => {
            this.alarmOptions = deviceAlarmGroups.flatMap((group, index) => {
                const device = this.seriesDevices[index];
                const fields = fieldsByDevice.get(device.id);
                return [...group.deviceAlarms, ...group.projectAlarms]
                    .filter((alarm) => fields?.has(alarm.field))
                    .map((alarm) => {
                        const key = `${device.id}:${alarm.id}`;
                        return {
                            key,
                            deviceId: device.id,
                            label: `${device.deviceName} - ${alarm.name}`,
                            alarm,
                            selected: selectedState.get(key) ?? false,
                        };
                    });
            });
            this.rebuildChart();
            this.cdr.detectChanges();
        });
    }

    private loadChartData() {
        const range = this.resolveSelectedRange();
        if (!range) {
            return;
        }

        if (this.seriesConfigs.length === 0) {
            this.loading = false;
            this.chartDataByDevice = {};
            this.rebuildChart();
            return;
        }

        const fieldsByDevice = this.getSeriesFieldsByDevice();
        this.loading = true;
        forkJoin(
            this.seriesDevices.map((device) =>
                this.dataService
                    .getDeviceDataRange(
                        device.id,
                        [...(fieldsByDevice.get(device.id) ?? [])],
                        range.start.toDate(),
                        range.end.toDate(),
                        this.groupData,
                    )
                    .pipe(catchError(() => of({} as DeviceChartData))),
            ),
        ).subscribe(
            (results) => {
                this.chartDataByDevice = {};
                results.forEach((result, index) => {
                    const deviceId = this.seriesDevices[index].id;
                    const normalized: DeviceChartData = {};
                    [...(fieldsByDevice.get(deviceId) ?? [])].forEach((field) => {
                        normalized[field] = [...(result[field] ?? [])].sort(
                            (left, right) => new Date(left.date).getTime() - new Date(right.date).getTime(),
                        );
                    });
                    this.chartDataByDevice[deviceId] = normalized;
                });
                this.loading = false;
                this.rebuildChart();
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.loading = false;
                this.cdr.detectChanges();
            },
        );
    }

    private resolveSelectedRange(): { start: moment.Moment; end: moment.Moment } | null {
        let start = moment();
        let end = moment();

        switch (this.dateRange) {
            case "last15minutes":
                start = moment().subtract(15, "minutes");
                end = moment();
                this.enableInstantValues = true;
                break;
            case "lasthour":
                start = moment().subtract(1, "hours");
                end = moment();
                this.enableInstantValues = true;
                break;
            case "last4hours":
                start = moment().subtract(4, "hours");
                end = moment();
                this.enableInstantValues = true;
                break;
            case "last12hours":
                start = moment().subtract(12, "hours");
                end = moment();
                this.enableInstantValues = true;
                break;
            case "last24hours":
                start = moment().subtract(24, "hours");
                end = moment();
                this.enableInstantValues = false;
                break;
            case "custom": {
                const parsedStart = this.parseCustomDateTime(this.startDate, this.startTime);
                const parsedEnd = this.parseCustomDateTime(this.endDate, this.endTime);
                if (!parsedStart || !parsedEnd || !parsedEnd.isAfter(parsedStart)) {
                    return null;
                }
                start = parsedStart;
                end = parsedEnd;
                this.enableInstantValues = end.diff(start, "hours", true) <= 4;
                break;
            }
        }

        if (!this.enableInstantValues && this.groupingData === "no") {
            this.groupingData = "5minutes";
        }

        this.startDate = moment(start);
        this.endDate = moment(end);
        this.updateEndDate();
        return { start, end };
    }

    private parseCustomDateTime(dateValue: moment.Moment | Date | null, timeValue: string): moment.Moment | null {
        const date = dateValue ? moment(dateValue) : null;
        const [hours, minutes] = (timeValue ?? "").split(":");
        if (!date?.isValid() || hours == null || minutes == null) {
            return null;
        }

        return date.clone().hour(Number(hours)).minute(Number(minutes)).second(0).millisecond(0);
    }

    private rebuildChart() {
        const chart = this.createBaseChartOptions();
        const dataSeries = this.buildDataSeries();
        const alarmSeries = this.buildAlarmSeries();

        chart.series = [...dataSeries, ...alarmSeries];
        this.hasChartData = dataSeries.length > 0;
        this.chart = chart;
        this.chartUpdate = true;
        this.cdr.detectChanges();
        this.refreshChartLayout();
    }

    private buildDataSeries(): Highcharts.SeriesOptionsType[] {
        const series: Highcharts.SeriesOptionsType[] = [];

        this.seriesConfigs.forEach((config, index) => {
            const data = this.chartDataByDevice[config.deviceId]?.[config.dataType] ?? [];
            const seriesData =
                config.variant === "base"
                    ? this.buildSeriesData(config.dataType, data)
                    : this.buildPercentileSeriesData(config.deviceId, config.dataType, config.variant);
            if (seriesData.length === 0) {
                return;
            }

            series.push({
                id: config.key,
                type: "line",
                name: this.getSeriesLabel(config),
                data: seriesData,
                color: config.color,
                dashStyle: config.lineStyle,
                lineWidth: config.variant === "base" ? 2 : 1.5,
                marker: {
                    enabled: false,
                },
                showInNavigator: index === 0,
            } as Highcharts.SeriesLineOptions);
        });

        return series;
    }

    private buildSeriesData(dataType: DataTypeModelKey, data: DeviceDataModel[]): Array<[number, number]> {
        if (this.groupData) {
            return data
                .filter((entry) => {
                    if (!entry.aggregation) {
                        return false;
                    }
                    const value = this.usesBucketMaxValue(dataType) ? entry.aggregation.max : entry.aggregation.average;
                    return value !== undefined && value !== null && !Number.isNaN(value);
                })
                .map(
                    (entry) =>
                        [
                            new Date(entry.date).getTime(),
                            this.roundValue(this.usesBucketMaxValue(dataType) ? entry.aggregation.max : entry.aggregation.average),
                        ] as [number, number],
                );
        }

        return data.flatMap((entry) => {
            if (entry.data?.length) {
                return [...entry.data]
                    .sort((left, right) => new Date(left.timestamp).getTime() - new Date(right.timestamp).getTime())
                    .map((point) => [new Date(point.timestamp).getTime(), this.roundValue(point.value)] as [number, number]);
            }

            if (entry.aggregation?.average === undefined || entry.aggregation?.average === null) {
                return [];
            }

            return [[new Date(entry.date).getTime(), this.roundValue(entry.aggregation.average)] as [number, number]];
        });
    }

    private buildPercentileSeriesData(
        deviceId: string,
        dataType: DataTypeModelKey,
        percentile: Extract<ChartSeriesVariant, "L10" | "L90">,
    ): Array<[number, number]> {
        const data = this.chartDataByDevice[deviceId]?.[dataType] ?? [];
        const percentileKey = percentile === "L10" ? "percentile90" : "percentile10";

        return data
            .filter((entry) => {
                const value = entry.aggregation?.[percentileKey];
                return value !== undefined && value !== null && !Number.isNaN(value);
            })
            .map((entry) => [new Date(entry.date).getTime(), this.roundValue(entry.aggregation[percentileKey])] as [number, number]);
    }

    private buildAlarmSeries(): Highcharts.SeriesOptionsType[] {
        return this.alarmOptions
            .filter((option) => option.selected)
            .filter((option) =>
                this.groupData
                    ? option.alarm.window === AlarmModelTimeWindow.AlarmModelTimeWindowBucket5
                    : option.alarm.window === AlarmModelTimeWindow.AlarmModelTimeWindowInstant,
            )
            .map(
                (option) =>
                    ({
                        id: `alarm:${option.key}`,
                        type: "line",
                        name: option.label,
                        data: buildAlarmThresholdSeries(option.alarm, this.project, this.startDate, this.endDate),
                        color: "#dc2626",
                        dashStyle: "ShortDash",
                        lineWidth: 1,
                        step: "left",
                        zIndex: 10,
                        marker: {
                            enabled: false,
                        },
                        showInNavigator: false,
                    }) as Highcharts.SeriesLineOptions,
            );
    }

    private getSeriesFieldsByDevice(): Map<string, Set<DataTypeModelKey>> {
        return this.seriesConfigs.reduce((accumulator, series) => {
            if (!accumulator.has(series.deviceId)) {
                accumulator.set(series.deviceId, new Set<DataTypeModelKey>());
            }
            accumulator.get(series.deviceId)!.add(series.dataType);
            return accumulator;
        }, new Map<string, Set<DataTypeModelKey>>());
    }

    private get seriesDevices(): ProjectDeviceModel[] {
        const deviceIds = new Set(this.seriesConfigs.map((series) => series.deviceId));
        return this.devices.filter((device) => deviceIds.has(device.id));
    }

    private getValidDeviceFields(device: ProjectDeviceModel): DataTypeModelKey[] {
        return [...(device.fields ?? [])]
            .filter((field) => !invalidProjectChartMetrics.has(field) && !isRetiredDataTypeModelKey(field))
            .sort((left, right) =>
                this.translation.translateService
                    .instant(`FIELD.${left}`)
                    .localeCompare(this.translation.translateService.instant(`FIELD.${right}`), this.locale),
            );
    }

    private buildMetricOptionKey(dataType: DataTypeModelKey, variant: ChartSeriesVariant): string {
        return `${dataType}:${variant}`;
    }

    private buildSeriesKey(deviceId: string, dataType: DataTypeModelKey, variant: ChartSeriesVariant): string {
        return `${deviceId}:${dataType}:${variant}`;
    }

    private buildSeriesKeyFromSelection(): string | null {
        const metric = this.availableMetricOptions.find((candidate) => candidate.key === this.selectedMetricKey);
        if (!this.selectedSeriesDeviceId || !metric) {
            return null;
        }

        return this.buildSeriesKey(this.selectedSeriesDeviceId, metric.dataType, metric.variant);
    }

    private getSeriesLabel(series: ProjectChartSeriesConfig): string {
        const fieldLabel = this.translation.translateService.instant(`FIELD.${series.dataType}`);
        const suffix = series.variant === "base" ? "" : ` (${series.variant})`;
        return `${series.deviceName} - ${fieldLabel}${suffix}`;
    }

    private getDefaultLineStyle(variant: ChartSeriesVariant): ChartSeriesLineStyle {
        switch (variant) {
            case "L10":
                return "ShortDash";
            case "L90":
                return "Dot";
            default:
                return "Solid";
        }
    }

    private getNextSeriesColor(): string {
        const usedColors = new Set(this.seriesConfigs.map((series) => series.color));
        const available = defaultPalette.find((color) => !usedColors.has(color));
        return available ?? defaultPalette[this.seriesConfigs.length % defaultPalette.length];
    }

    private usesBucketMaxValue(dataType: DataTypeModelKey): boolean {
        return (
            dataType === DataTypeModelKey.rolling5s ||
            dataType === DataTypeModelKey.rolling5sC ||
            dataType === DataTypeModelKey.rolling5 ||
            dataType === DataTypeModelKey.rolling5C ||
            dataType === DataTypeModelKey.rolling30 ||
            dataType === DataTypeModelKey.rolling30C
        );
    }

    private usesLogarithmicAverage(dataType: DataTypeModelKey): boolean {
        return (
            dataType === DataTypeModelKey.soundPressureLevel ||
            dataType === DataTypeModelKey.soundPressureLevelC ||
            dataType === DataTypeModelKey.soundPressureLevel1Minute ||
            dataType === DataTypeModelKey.soundPressureLevel1MinuteSensor2 ||
            dataType === DataTypeModelKey.soundPressureLevelC1Minute
        );
    }

    private roundValue(value: number): number {
        return Math.round(value * 10) / 10;
    }

    private createBaseChartOptions(): Highcharts.Options {
        return {
            chart: {
                type: "spline",
                height: 560,
                zoomType: "xy",
                backgroundColor: "transparent",
            } as any,
            rangeSelector: {
                enabled: false,
            },
            navigator: {
                enabled: true,
            },
            scrollbar: {
                enabled: true,
            },
            credits: {
                enabled: false,
            },
            legend: {
                enabled: true,
                itemStyle: {
                    fontSize: "14px",
                    fontWeight: "500",
                },
            },
            time: {
                useUTC: false,
            } as any,
            tooltip: {
                shared: true,
                valueDecimals: 1,
                style: {
                    fontSize: "15px",
                },
            },
            plotOptions: {
                line: {
                    gapSize: 0,
                    gapUnit: "value",
                    dataGrouping: {
                        enabled: false,
                    },
                },
                series: {
                    turboThreshold: 0,
                    marker: {
                        enabled: false,
                    },
                },
            },
            xAxis: {
                type: "datetime",
                ordinal: false,
                labels: {
                    style: {
                        fontSize: "13px",
                    },
                },
            },
            yAxis: [
                {
                    title: {
                        text: null,
                    },
                    labels: {
                        format: "{value} dB",
                        style: {
                            fontSize: "13px",
                        },
                    },
                },
            ],
            series: [],
        };
    }

    private get groupData(): boolean {
        return this.groupingData === "5minutes";
    }
}
